'use client';

import React from 'react';
import Link from 'next/link';
import { Container } from '@/components/ui/Container';
import { Text } from '@/components/ui/Text';
import { Locale, getLocalePath } from '@/lib/i18n';
import { resolveGoogleReview } from '@/lib/google-reviews';
import { resetConsent } from '@/lib/consent';
import styles from './Footer.module.css';

export interface FooterProps {
  locale: Locale;
  solutions?: Array<{ label: string; href: string }>;
  industries?: Array<{ label: string; href: string }>;
  integrations?: Array<{ label: string; href: string }>;
  resources?: Array<{ label: string; href: string }>;
  legal?: Array<{ label: string; href: string }>;
  messages: any;
}

/**
 * Site Footer with navigation and social links
 * SEO-optimized with comprehensive sitemap
 */
export function Footer({ locale, solutions, industries, integrations, resources, legal, messages }: FooterProps) {
  const currentYear = new Date().getFullYear();
  const googleReview = resolveGoogleReview(locale, messages?.footer?.googleReview);

  return (
    <footer className={styles.footer} role="contentinfo">
      <Container>
        <div className={styles.content}>
          {/* Brand */}
          <div className={styles.brand}>
            <Link href={getLocalePath(locale, '/')} className={styles.logo}>
              <span className={styles.logoText}>Jamezz</span>
            </Link>
            
            {/* Google Review Badge */}
            <div className={styles.googleReviewBadge}>
              <svg className={styles.googleIcon} viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div className={styles.reviewInfo}>
                <div className={styles.stars}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className={styles.star}>★</span>
                  ))}
                </div>
                <span className={styles.reviewText}>
                  {googleReview.rating} {googleReview.text} {googleReview.reviews}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation Columns */}
          <div className={styles.columns}>
            {/* Solutions Column */}
            {solutions && solutions.length > 0 && (
              <div className={styles.column}>
                <h3 className={styles.columnTitle}>{messages.footer.solutions.title}</h3>
                <ul className={styles.links}>
                  {solutions.map((item) => (
                    <li key={item.href}>
                      <Link href={getLocalePath(locale, item.href)} className={styles.link}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Industries Column */}
            {industries && industries.length > 0 && (
              <div className={styles.column}>
                <h3 className={styles.columnTitle}>{messages.footer.industries.title}</h3>
                <ul className={styles.links}>
                  {industries.map((item) => (
                    <li key={item.href}>
                      <Link href={getLocalePath(locale, item.href)} className={styles.link}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Integrations Column */}
            {integrations && integrations.length > 0 && (
              <div className={styles.column}>
                <h3 className={styles.columnTitle}>{messages.footer.integrations.title}</h3>
                <ul className={styles.links}>
                  {integrations.map((item) => (
                    <li key={item.href}>
                      <Link href={getLocalePath(locale, item.href)} className={styles.link}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Resources Column */}
            {resources && resources.length > 0 && (
              <div className={styles.column}>
                <h3 className={styles.columnTitle}>{messages.footer.resources.title}</h3>
                <ul className={styles.links}>
                  {resources.map((item) => (
                    <li key={item.href}>
                      <Link href={getLocalePath(locale, item.href)} className={styles.link}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Legal Column */}
            {legal && legal.length > 0 && (
              <div className={styles.column}>
                <h3 className={styles.columnTitle}>{messages.footer.legal.title}</h3>
                <ul className={styles.links}>
                  {legal.map((item) => (
                    <li key={item.href}>
                      <Link href={getLocalePath(locale, item.href)} className={styles.link}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                  {/* Cookie Settings Link */}
                  <li>
                    <button 
                      onClick={() => {
                        // Open consent banner to allow users to change their preferences
                        // Don't reset consent - let ConsentBanner handle the logic
                        window.dispatchEvent(new CustomEvent('openConsentBanner'));
                      }}
                      className={styles.link}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      {messages.footer.legal.cookieSettings || 'Cookie Settings'}
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={styles.bottom}>
          <div className={styles.contactLinks} aria-label="Contact methods">
            <a className={styles.contactLink} href="mailto:info@jamezz.com">
              info@jamezz.com
            </a>
            <span className={styles.contactDivider} aria-hidden="true">
              •
            </span>
            <a className={styles.contactLink} href="tel:+31853013000">
              +31 85 301 3000
            </a>
          </div>
          <Text size="sm" color="tertiary" className={styles.tagline}>
            {messages.footer.tagline}
          </Text>
          <Text size="xs" color="tertiary">
            © {currentYear} Jamezz. {messages.footer.copyright}
          </Text>
        </div>
      </Container>
    </footer>
  );
}
